import { WizardData } from '@/src/types';
import {
  zExperienceDSL_v10,
  zExperienceDSL_v11,
  ExperienceDSL,
  RITUAL_TYPES,
  ExperienceDSLV10,
  ExperienceDSLV11,
  MOMENT_TYPES,
  OCCASIONS,
  Occasion,
} from './dsl';

/** Occasions whose meta requires a second name (a wedding has two people; a birthday does not). */
const REQUIRES_SECONDARY_NAME: Occasion[] = ['wedding', 'anniversary', 'engagement'];

function toOccasion(value: unknown): Occasion | undefined {
  if (typeof value !== 'string') return undefined;
  const lower = value.toLowerCase();
  return (OCCASIONS as readonly string[]).includes(lower) ? (lower as Occasion) : undefined;
}

/** Splits "A & B" / "A and B" into two names. Used to seed couple-shaped occasions. */
function splitPair(value: string | undefined): [string, string | undefined] {
  if (!value) return ['', undefined];
  const parts = value.split(/ (?:&|and) /i);
  return [parts[0]?.trim() ?? '', parts[1]?.trim() || undefined];
}

export interface DslValidationError {
  path: (string | number)[];
  message: string;
}

export interface DslValidationResult {
  success: boolean;
  data?: ExperienceDSL;
  errors?: DslValidationError[];
}

/**
 * buildExperienceDsl — scaffolds a DSL from WizardData.
 *
 * Wedding keeps emitting **v1.0** so its behaviour and snapshots stay byte-identical.
 * Every other occasion emits **v1.1**, whose `moments` are occasion-specific — a birthday
 * has `cake_moment`, not `saptapadi`.
 *
 * Previously this only ever produced a wedding v1.0 DSL, so a birthday invitation either
 * failed validation or carried wedding rituals that no birthday art direction could match.
 *
 * The prompt-generator and scene-compiler call upgradeDSL() internally, so callers do not
 * need to care which version comes back.
 */
export function buildExperienceDsl(data: WizardData): DslValidationResult {
  const expData: Partial<ExperienceDSLV10> = (data.customData?.experience as any) || {};
  const meta = (expData.meta || {}) as Partial<ExperienceDSLV10['meta']>;

  const occasion =
    toOccasion((data.customData?.experience as any)?.occasion)
    ?? toOccasion(data.occasionType);

  if (occasion && occasion !== 'wedding') {
    return buildV11Dsl(data, occasion, expData);
  }

  // Provide sensible defaults from top-level fields
  const brideName = meta.brideName || (data.hostName ? data.hostName.split(/ (?:&|and) /i)[0] : '');
  const groomName = meta.groomName || (data.hostName ? data.hostName.split(/ (?:&|and) /i)[1] : '') || '';
  const weddingDate = meta.weddingDate || (data.eventDate ? new Date(data.eventDate).toISOString().split('T')[0] : '');

  const raw: ExperienceDSLV10 = {
    version: '1.0',
    template: (expData.template as any) || 'royal',
    plan: 'pro',
    meta: {
      brideName,
      groomName,
      weddingDate,
      tagline: meta.tagline || data.customMessage || '',
      referenceImages: meta.referenceImages || {},
    },
    rituals: expData.rituals || RITUAL_TYPES.map((ritualId, order) => ({
      ritualId,
      order,
      date: weddingDate,
      venue: {
        name: data.eventVenue || 'Venue Details',
        address: data.eventVenue || 'Location',
      },
      emotionalArc: { primary: 'festive' as const, intensity: 'moderate' as const },
    })),
  };

  const result = zExperienceDSL_v10.safeParse(raw);

  if (!result.success) {
    return {
      success: false,
      errors: result.error.issues.map((err: any) => ({
        path: err.path,
        message: err.message,
      })),
    };
  }

  return {
    success: true,
    data: result.data,
  };
}

/**
 * Builds a v1.1 DSL for any non-wedding occasion.
 *
 * Moments default to the full set the occasion declares in MOMENT_TYPES, so every section
 * of the rendered experience has art direction behind it (see ceremony-prompts.ts). An
 * explicit `moments` array in customData overrides that.
 */
function buildV11Dsl(
  data: WizardData,
  occasion: Occasion,
  expData: Partial<ExperienceDSLV10> & { moments?: ExperienceDSLV11['moments'] },
): DslValidationResult {
  const metaIn = ((expData as any).meta || {}) as Partial<ExperienceDSLV11['meta']>;

  const [splitPrimary, splitSecondary] = splitPair(data.hostName);

  // A birthday has one celebrant; a wedding-shaped occasion has two. Fall back to the
  // event name before the host name — for a birthday the celebrant is the subject of the
  // event, not its host.
  const primaryName =
    metaIn.primaryName
    || (occasion === 'anniversary' || occasion === 'engagement' ? splitPrimary : '')
    || data.eventName
    || splitPrimary
    || 'Our Celebration';

  const secondaryName =
    metaIn.secondaryName
    || (REQUIRES_SECONDARY_NAME.includes(occasion) ? splitSecondary : undefined);

  const eventDate =
    metaIn.eventDate
    || (data.eventDate ? new Date(data.eventDate).toISOString().split('T')[0] : '');

  const venue = data.eventVenue
    ? { name: data.eventVenue, address: data.eventVenue }
    : undefined;

  const raw: ExperienceDSLV11 = {
    version: '1.1',
    template: ((expData as any).template as string) || 'royal',
    plan: 'pro',
    meta: {
      occasion,
      primaryName,
      ...(secondaryName ? { secondaryName } : {}),
      eventDate,
      tagline: metaIn.tagline || data.customMessage || '',
      referenceImages: metaIn.referenceImages || {},
    },
    moments: expData.moments || MOMENT_TYPES[occasion].map((momentId, order) => ({
      momentId,
      order,
      date: eventDate,
      ...(venue ? { venue } : {}),
      emotionalArc: { primary: 'festive' as const, intensity: 'moderate' as const },
    })),
  };

  const result = zExperienceDSL_v11.safeParse(raw);

  if (!result.success) {
    return {
      success: false,
      errors: result.error.issues.map((err: any) => ({
        path: err.path,
        message: err.message,
      })),
    };
  }

  return { success: true, data: result.data };
}
